import { formatTime } from "@/lib/time/format";
import { ContentType, DigestTheme, PulseType } from "@/models/market-intelligence";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import PercentIcon from "@mui/icons-material/Percent";
import SpeedIcon from "@mui/icons-material/Speed";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BoltIcon from "@mui/icons-material/Bolt";
import CallMergeIcon from "@mui/icons-material/CallMerge";
import InsightsIcon from "@mui/icons-material/Insights";
import GroupIcon from "@mui/icons-material/Group";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DescriptionIcon from "@mui/icons-material/Description";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

export const pulseTypeIcon = (type: PulseType) => {
    const iconProps = { fontSize: "inherit" as const };
    switch (type) {
        case "short_interest":
            return <TrendingDownIcon {...iconProps} />;
        case "ctb":
            return <PercentIcon {...iconProps} />;
        case "utilisation":
            return <SpeedIcon {...iconProps} />;
        case "options":
            return <ShowChartIcon {...iconProps} />;
        case "analyst":
            return <AccountBalanceIcon {...iconProps} />;
        case "signal":
            return <BoltIcon {...iconProps} />;
        case "convergence":
            return <CallMergeIcon {...iconProps} />;
        case "alt_data":
            return <InsightsIcon {...iconProps} />;
        case "institutional":
            return <GroupIcon {...iconProps} />;
        default:
            return <BoltIcon {...iconProps} />;
    }
};

export const pulseTypeLabel = (type: PulseType): string => {
    const labels: Record<PulseType, string> = {
        "": "Pulse",
        short_interest: "Short Interest",
        analyst: "Analyst",
        options: "Options",
        institutional: "Institutional",
        ctb: "Cost to Borrow",
        utilisation: "Utilisation",
        signal: "Signal",
        convergence: "Convergence",
        alt_data: "Alt Data",
    };
    return labels[type] || "Pulse";
};

export const contentTypeLabel = (type: ContentType): string => {
    const labels: Record<ContentType, string> = {
        pulse: "Pulse",
        earnings_preview: "Earnings Preview",
        trader_note: "Trader Note",
        weekly_digest: "Weekly Digest",
        convergence_report: "Convergence",
    };
    return labels[type] || humanizeKey(type);
};

/** Fallback label for a key the maps above don't know yet: market_commentary → Market Commentary. */
const humanizeKey = (key: string): string =>
    key
        .toLowerCase()
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());

export const contentTypeIcon = (type: ContentType) => {
    const iconProps = { fontSize: "inherit" as const, style: { verticalAlign: "middle" } };
    switch (type) {
        case "earnings_preview":
            return <CalendarTodayIcon {...iconProps} />;
        case "trader_note":
            return <DescriptionIcon {...iconProps} />;
        case "weekly_digest":
            return <NewspaperIcon {...iconProps} />;
        case "convergence_report":
            return <CallMergeIcon {...iconProps} />;
        default:
            return <BoltIcon {...iconProps} />;
    }
};

export const themeLabel = (theme: DigestTheme): string => {
    const labels: Record<DigestTheme, string> = {
        "": "",
        overall: "Overall",
        short_interest: "Short Interest",
        analyst: "Analyst",
        options: "Options & Sentiment",
        convergence: "Convergence",
    };
    return labels[theme] || humanizeKey(theme);
};

export const dataSourceLabel = (key: string): string => {
    const labels: Record<string, string> = {
        short_interest: "Short interest",
        cost_to_borrow: "Cost to borrow",
        utilisation: "Lending utilisation",
        options: "Options & sentiment",
        signals: "ORTEX Alpha signals",
        earnings: "Earnings history",
        analyst: "Analyst consensus",
        institutional: "Institutional positioning",
        alt_data: "Alternative data",
        company: "Company fundamentals",
        price: "Price action",
    };
    return labels[key] || key;
};

export const modelDisplayName = (modelId: string): string => {
    if (modelId.includes("claude-opus")) return "Claude Opus";
    if (modelId.includes("claude-sonnet")) return "Claude Sonnet";
    if (modelId.includes("claude-haiku")) return "Claude Haiku";
    return modelId;
};

export const formatRelativeTime = (isoDate: string | null): string => {
    if (!isoDate) return "";
    return dayjs(isoDate).fromNow();
};

export const formatDateFriendly = (isoDate: string | null): string => {
    if (!isoDate) return "";
    // Was a device-local time labelled "UTC" (#1281).
    return formatTime(isoDate, "D MMM YYYY, HH:mm", { withZone: true });
};

export function stripMarkdown(md: string): string {
    return md
        .replace(/#{1,6}\s+/g, "")
        .replace(/\*\*(.*?)\*\*/g, "$1")
        .replace(/\*(.*?)\*/g, "$1")
        .replace(/`([^`]+)`/g, "$1")
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
        .replace(/^[-*+]\s+/gm, "")
        .replace(/^\d+\.\s+/gm, "")
        .replace(/^>\s+/gm, "")
        .replace(/\n+/g, " ")
        .trim();
}
